/**
 * School modal for Product Insights · LAE (Productivity, Schools mode). Owner C11 (C14 merged in).
 *
 * Ports LAE `features/productivity/SchoolModal.jsx` (the shell, the block layout, `StatusMoves` and
 * `StatusHours`), `shared/useSchoolModalHero.js` (the hero row pick) and `charts/StatusHourChart.jsx`
 * (the Moves by hour bars, inline here).
 *
 * As in LAE, the header carries the PAGE's period control and the page's Source switch: changing either
 * here changes the page too. The modal has no cycle or funnel control of its own.
 *
 * The `description` line (secondary name · funnel · cycle · period) is a "facts about the data" line,
 * not helper text: keep it in any helper-text sweep.
 *
 * Deliberate differences from LAE:
 * - No drill-downs (owner 2026-10-29): `useDrilldown`, `onDrill*`, `cellScope` and the tile clicks are
 *   gone; counts are plain text. No counsellor modal: counsellor rows are not clickable, and the
 *   Escape-ownership rule for a stacked counsellor modal is gone with it.
 * - No InfoTips and no tab hovers (spec §0.4). No tile editing (`stripId`).
 * - Hero tile values come from the page's already-loaded list (`listData`) through `pickSchoolRow`, so
 *   the modal matches the table row; LAE re-read the list from its query cache.
 * - Layout through EMERGE `usePageLayout('product-insights.lae.school-modal')`; the trend metric, the
 *   legend and the Moves by hour status pick are remembered per account with `useRememberedState`.
 * - Every block has its own loading skeleton instead of one `DashboardSkeleton` for the whole body.
 * - Status moves is the fab-ui `BarList` (it also shows each row's share).
 * - When the school maps to a SEED EMERGE company (owner 2026-10-29), the title is a real link to it.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Combobox, EmptyState, Modal, SegmentedControl, Skeleton } from '@/components/ui';
import { LayoutGrid, LayoutToolbar } from '@/components/layout';
import { usePageLayout, type LayoutBlockDefault } from '@/lib/use-page-layout';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import type {
  LaeFunnel,
  LaeProdEmergeCompany,
  LaeProdSource,
  LaeProdStatuses,
  LaeProdStatusHours,
  LaeProductivityQuery,
  LaeProductivitySchoolsResponse,
} from '@/lib/types';
import { PiError, PiLoading } from '../pi-kit';
import { BarList, type BarListRow } from '../fab-ui';
import { ColumnChart } from './ColumnChart';
import { KpiStrip, KpiStripSkeleton } from './KpiStrip';
import { OutcomeHours } from './OutcomeHours';
import { OutcomesBlock } from './OutcomesBlock';
import { PeriodControl } from './PeriodControl';
import { SchoolCounsellorsTable } from './SchoolCounsellorsTable';
import { SchoolsTable } from './SchoolsTable';
import { TrendCard } from './TrendCard';
import { bucketTarget } from './lae-dates';
import { fmtNum, fmtRange, splitSchool } from './lae-format';
import { buildSchoolHero } from './lae-hero';
import { hourLabel, peakSpan } from './lae-hour-pattern';
import type { LaeProductivityParams } from './lae-params';
import { pickSchoolRow, schoolGroupsOf } from './lae-school-rows';
import { useLaeProductivitySchool } from './use-lae-productivity-data';

/** Block ids are stored per person: never rename one. */
const DEFAULTS: readonly LayoutBlockDefault[] = [
  { id: 'kpis', label: 'Key figures', width: 'full' },
  { id: 'outcomes-table', label: 'Outcomes', width: 'full' },
  { id: 'trend', label: 'Activity', width: 'full' },
  { id: 'best-hours', label: 'Best hours', width: 'full' },
  { id: 'status-hours', label: 'Moves by hour', width: 'full' },
  { id: 'counsellors', label: 'Counsellors', width: 'full' },
  { id: 'assignments', label: 'Assignments', width: 'full' },
  { id: 'status-moves', label: 'Status moves', width: 'full' },
];
const PAGE_ID = 'product-insights.lae.school-modal';

const ADMIT_FUNNEL = 'admit_to_enrollment';
const FUNNELS: readonly LaeFunnel[] = ['all', 'prospect_to_application', 'admit_to_enrollment'];
const HERO_TILES = 15;
const TOP_STATUSES = 8;
const TOP_FROM = 6;
const HOUR_SLOTS = Array.from({ length: 24 }, (_, h) => h);
const HOUR_BUCKETS = HOUR_SLOTS.map(String);
const HOUR_TICKS = [0, 4, 8, 12, 16, 20] as const;

const SOURCE_OPTIONS: readonly { value: LaeProdSource; label: string }[] = [
  { value: 'all', label: 'All calls' },
  { value: 'system', label: 'System' },
  { value: 'manual', label: 'Manual' },
];

/** "No previous status" reaches us as null, '' or a marker string; a bare render would print "null". */
const NO_PREVIOUS = /^(null|none|unknown|unset|__none__|__unset__|-|–|—)$/i;

const statusLabel = (value: string | null | undefined): string => String(value ?? '').trim() || '—';

function fromLabel(value: string | null | undefined): string {
  const s = String(value ?? '').trim();
  return !s || NO_PREVIOUS.test(s) ? 'No previous status' : s;
}

/** 'prospect_to_application' → 'Prospect to application'; '' (all funnels) → ''. */
const funnelLabel = (v: string): string => (v ? v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' ') : '');

const asFunnel = (v: string | null | undefined): LaeFunnel | undefined =>
  FUNNELS.find((f) => f === v);

const countOf = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

const parseString = (raw: unknown): string | undefined => (typeof raw === 'string' ? raw : undefined);

function errorMessage(error: unknown): string {
  if (error instanceof ApiError && error.message) return error.message;
  return 'Lead & Application Engagement could not be reached.';
}

/** Every block after the key figures: a hairline above, room below it. */
function Block({ children }: { children: ReactNode }) {
  return <section className="h-full min-w-0 border-t border-border pt-5">{children}</section>;
}

function SectionTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h3 className={cx('mb-2 text-sm font-semibold text-fg', className)}>{children}</h3>;
}

function TrendSkeleton({ height }: { height: number }) {
  return (
    <div aria-hidden="true" className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Skeleton className="h-3.5 w-32" />
        <Skeleton className="h-8 w-64 max-w-full" />
      </div>
      <div className="animate-pulse rounded-md bg-surface-sunken motion-reduce:animate-none" style={{ height }} />
    </div>
  );
}

function StatusMoves({ statuses, loading }: { statuses: LaeProdStatuses | null | undefined; loading: boolean }) {
  const rows = useMemo<BarListRow[]>(() => {
    const values = [...(statuses?.values ?? [])].sort((a, b) => countOf(b.records) - countOf(a.records));
    const top: BarListRow[] = values.slice(0, TOP_STATUSES).map((v, i) => ({
      key: `${i}:${v.value ?? ''}`,
      label: v.value || '—',
      value: countOf(v.records),
    }));
    const rest = values.slice(TOP_STATUSES).reduce((s, v) => s + countOf(v.records), 0);
    if (rest > 0) top.push({ key: '__other__', label: 'Other', value: rest });
    return top;
  }, [statuses]);

  return <BarList title="Status moves" rows={rows} loading={loading} emptyText="No status moves" />;
}

interface StatusRow {
  key: string;
  label: string;
  records: number;
  values: number[];
  from: { label: string; records: number }[];
}

/**
 * Moves by hour: at what hours of the day leads moved into one chosen status, and what they were
 * before. `status_hours` is not produced by the backend yet, so this usually renders nothing; the
 * block gates on the same `by_status` length so the layout grid sees a null.
 * The headline total is `records` (distinct leads), never the sum of `hours` (moves).
 */
function StatusHours({ statusHours }: { statusHours: LaeProdStatusHours }) {
  const [picked, setPicked] = useRememberedState<string>('lae-modal-status-pick', '', parseString);

  // Re-sorted here as well: the default pick is "the biggest", so it cannot rest on the server's order.
  const rows = useMemo<StatusRow[]>(() => {
    const list = (statusHours.by_status ?? []).map((r) => {
      const byHour = new Map<number, number>((r.hours ?? []).map((h) => [Number(h.hour), countOf(h.records)]));
      return {
        key: String(r.value ?? ''),
        label: statusLabel(r.value),
        records: countOf(r.records),
        values: HOUR_SLOTS.map((h) => byHour.get(h) ?? 0),
        from: (r.from ?? []).map((f) => ({ label: fromLabel(f.value), records: countOf(f.records) })),
      };
    });
    return list.sort(
      (a, b) => b.records - a.records || a.label.localeCompare(b.label, 'en-GB', { sensitivity: 'base' }),
    );
  }, [statusHours]);

  // A remembered status this window never saw falls back to the biggest rather than a blank chart.
  const active = (picked ? rows.find((r) => r.key === picked) : undefined) ?? rows[0];

  const options = useMemo(
    () =>
      rows.map((r) => ({
        value: r.key,
        label: r.label,
        hint: `${fmtNum(r.records)} ${r.records === 1 ? 'lead' : 'leads'}`,
      })),
    [rows],
  );
  const peak = useMemo(() => (active ? peakSpan(active.values) : null), [active]);
  // Labels merge before the cut, so two shapes of "no previous status" read as one entry.
  const sources = useMemo(() => {
    const merged = new Map<string, number>();
    for (const f of active?.from ?? []) merged.set(f.label, (merged.get(f.label) ?? 0) + f.records);
    const list = [...merged.entries()]
      .map(([label, records]) => ({ label, records }))
      .sort((a, b) => b.records - a.records);
    const top = list.slice(0, TOP_FROM);
    const rest = list.slice(TOP_FROM).reduce((s, f) => s + f.records, 0);
    return rest > 0 ? [...top, { label: 'Other', records: rest }] : top;
  }, [active]);
  const pickerId = 'lae-modal-status-pick';

  if (!active) return null;

  const series = [{ key: 'records', label: 'Moves', values: active.values, fill: 'var(--chart-1)' }];

  return (
    <>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <SectionTitle className="mb-0">
          Moves by hour <span className="text-xs font-normal text-fg-subtle">India time</span>
        </SectionTitle>
        <div className="w-full sm:w-72">
          <label htmlFor={pickerId} className="sr-only">
            Status
          </label>
          <Combobox id={pickerId} options={options} value={active.key} onChange={(v) => setPicked(v)} />
        </div>
      </div>

      <p className="text-sm text-fg-muted">
        <span className="font-medium text-fg tabular-nums">{fmtNum(active.records)}</span>{' '}
        {active.records === 1 ? 'lead' : 'leads'} moved into <span className="text-fg">{active.label}</span>
        {peak && (
          <>
            {' · busiest '}
            <span className="text-fg tabular-nums">{peak}</span>
          </>
        )}
      </p>

      <ColumnChart
        buckets={HOUR_BUCKETS}
        series={series}
        height={140}
        formatX={(b) => hourLabel(Number(b)).slice(0, 2)}
        formatTooltipX={(b) => hourLabel(Number(b))}
        formatValue={(v) => fmtNum(v)}
        xTicks={HOUR_TICKS}
        ariaLabel={`Leads moved into ${active.label} by hour of day`}
        className="mt-2"
      />

      {sources.length > 0 && (
        <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-fg-muted">
          <span className="text-fg-subtle">From</span>
          {sources.map((f) => (
            <span key={f.label} className="whitespace-nowrap">
              {f.label} <span className="text-fg tabular-nums">({fmtNum(f.records)})</span>
            </span>
          ))}
        </div>
      )}
    </>
  );
}

export interface SchoolModalProps {
  /** The platform school id (URL `school`). */
  schoolId: string;
  /** The funnel of the row that opened the modal (URL `schoolFunnel`); `''` = none given. */
  funnel: string;
  /** The page's params: period, cycle, source and their setters. */
  params: LaeProductivityParams;
  /** The page's already-loaded Schools list; the hero row and the groups come from it. */
  listData: LaeProductivitySchoolsResponse | null | undefined;
  onClose: () => void;
}

export function SchoolModal({ schoolId, funnel: rowFunnel, params, listData, onClose }: SchoolModalProps) {
  const layout = usePageLayout(PAGE_ID, DEFAULTS);
  const [reloadToken, setReloadToken] = useState(0);

  // A Schools row opens the modal for its own funnel; otherwise the page's funnel applies.
  const funnel: LaeFunnel = asFunnel(rowFunnel) ?? params.apiParams.funnel ?? 'all';
  const funnelValue = funnel === 'all' ? '' : funnel;
  const isAdmit = funnel === ADMIT_FUNNEL;

  const query = useMemo<LaeProductivityQuery>(() => {
    const { breakdown: _breakdown, ...rest } = params.apiParams;
    return { ...rest, funnel };
  }, [params.apiParams, funnel]);

  const { data, loading, refreshing, error } = useLaeProductivitySchool(schoolId, query, reloadToken);

  const { from, to } = params.range;
  const listSchool = listData?.schools.find((s) => String(s.school_id) === String(schoolId));
  const groups = useMemo(() => schoolGroupsOf(listData, schoolId, rowFunnel), [listData, schoolId, rowFunnel]);
  const schoolName = data?.school.school_name ?? groups[0]?.school_name ?? listSchool?.school_name ?? '';
  const { primary, secondary } = splitSchool(schoolName);
  const title = primary || 'School';

  const company: LaeProdEmergeCompany | null =
    data?.school.emerge_company ?? groups[0]?.emerge_company ?? listSchool?.emerge_company ?? null;
  const facts = [secondary, funnelLabel(funnelValue), params.cycle || 'All cycles', fmtRange(from, to)].filter(
    Boolean,
  );
  const description = facts.join(' · ');
  // The mapped SEED EMERGE company (owner 2026-10-29): the school name itself links to it.
  const titleNode: ReactNode = company ? (
    <Link
      to={`/companies/${company.id}`}
      onClick={(e) => e.stopPropagation()}
      className="underline-offset-2 hover:underline"
    >
      {title}
    </Link>
  ) : (
    title
  );

  const heroRow = useMemo(() => pickSchoolRow(listData, schoolId, rowFunnel, data), [listData, schoolId, rowFunnel, data]);
  const series = data?.totals.series;
  const granularity = data?.window.granularity ?? params.granularity;
  const heroItems = useMemo(
    () =>
      buildSchoolHero({
        totals: heroRow ?? {},
        prev: null,
        series: series ?? [],
        schools: heroRow ? [heroRow] : [],
        prevSchools: null,
        loading: !heroRow,
        granularity,
        single: true,
      }),
    [heroRow, series, granularity],
  );

  const onBucketClick = (bucket: string) => {
    const target = bucketTarget(bucket, granularity, params.today);
    if (target) params.setPeriod(target);
  };

  const half = (id: string) => layout.widthOf(id) === 'half';

  let body: ReactNode;
  if (error && !data) {
    body = <PiError message={errorMessage(error)} onRetry={() => setReloadToken((n) => n + 1)} />;
  } else {
    const waiting = !data;
    const render: Record<string, () => ReactNode | null> = {
      kpis: () =>
        waiting && !heroRow ? (
          <KpiStripSkeleton count={HERO_TILES} half={half('kpis')} />
        ) : (
          <KpiStrip items={heroItems} loading={!heroRow} half={half('kpis')} />
        ),
      // OutcomesBlock draws its own "Outcomes" heading.
      'outcomes-table': () => (
        <Block>
          {waiting ? (
            <PiLoading variant="table" rows={4} />
          ) : (
            <OutcomesBlock groups={groups} funnel={funnelValue} totals={heroRow} />
          )}
        </Block>
      ),
      trend: () => (
        <Block>
          {waiting ? (
            <TrendSkeleton height={200} />
          ) : (
            <TrendCard
              series={data.totals.series}
              buckets={data.window.buckets}
              granularity={granularity}
              onBucketClick={onBucketClick}
              height={200}
              admit={isAdmit}
              memoryPrefix="lae-modal"
              half={half('trend')}
            />
          )}
        </Block>
      ),
      // Absent outcome_hours: no block at all rather than an empty frame.
      'best-hours': () =>
        data?.outcome_hours ? (
          <Block>
            <SectionTitle>Best hours for positive outcomes</SectionTitle>
            <OutcomeHours data={data.outcome_hours} />
          </Block>
        ) : null,
      'status-hours': () =>
        data?.status_hours?.by_status?.length ? (
          <Block>
            <StatusHours statusHours={data.status_hours} />
          </Block>
        ) : null,
      // SchoolCounsellorsTable draws its own "Counsellors" heading.
      counsellors: () => (
        <Block>
          {waiting ? (
            <PiLoading variant="table" rows={6} />
          ) : (
            <SchoolCounsellorsTable rows={data.counsellors} funnel={funnelValue} />
          )}
        </Block>
      ),
      assignments: () => (
        <Block>
          {waiting ? (
            <PiLoading variant="table" rows={6} />
          ) : groups.length > 0 ? (
            <SchoolsTable
              title="Assignments"
              groups={groups}
              schools={[]}
              totals={heroRow}
              funnel={funnelValue}
              split
              scope="product-insights.lae.school-modal.assignments"
              memoryId="lae-modal-assignments"
              defaultSort={{ key: 'calls', dir: 'desc' }}
              footer={false}
            />
          ) : (
            <>
              <SectionTitle>Assignments</SectionTitle>
              <EmptyState title="No assignments" />
            </>
          )}
        </Block>
      ),
      'status-moves': () => (
        <Block>
          <StatusMoves statuses={data?.statuses} loading={waiting} />
        </Block>
      ),
    };

    body = (
      <div className="grid gap-4">
        {error ? <PiError message={errorMessage(error)} onRetry={() => setReloadToken((n) => n + 1)} /> : null}
        <div
          aria-busy={loading || refreshing || undefined}
          className={cx('transition-opacity motion-reduce:transition-none', refreshing && 'opacity-60')}
        >
          <LayoutGrid layout={layout} render={render} />
        </div>
      </div>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={titleNode}
      description={description}
      size="xl"
      headerActions={
        <>
          <PeriodControl
            compact
            preset={params.preset}
            day={params.day}
            range={params.range}
            onChange={params.setPeriod}
            onStep={params.stepDay}
          />
          <SegmentedControl
            label="Calls"
            size="sm"
            value={params.source}
            options={SOURCE_OPTIONS}
            onChange={(v) => params.setSource(v)}
            className="w-auto max-w-full"
          />
          {/* Kept while editing even if a refetch drops the data, so Done never vanishes mid-edit. */}
          {(data || layout.editing) && <LayoutToolbar layout={layout} compact />}
        </>
      }
    >
      {body}
    </Modal>
  );
}

export default SchoolModal;
